import logFetchError from "../Functions/LogFetchError";
import fetchSubmitLogs from "../Functions/FetchSubmitLogs";
import { fetchCaseInfo } from "../CaseInfoCache";

// Session sizing — mirrors the block-size limit used by Multi CCS.
export const MIN_BLOCK_SIZE = 2;
export const MAX_BLOCK_SIZE = 13;
export const TIMER_SPEED_OPTIONS = ["0.25x", "0.5x", "1x", "1.5x", "2x"];

export function timerSpeedMultiplier(speed) {
  switch (speed) {
    case "0.25x":
      return 0.25;
    case "0.5x":
      return 0.5;
    case "1.5x":
      return 1.5;
    case "2x":
      return 2;
    case "1x":
    default:
      return 1;
  }
}

// Case statuses used inside CaseProgress.Cases[].Status
export const CASE_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
};

// -----------------------------------------------------------------------------
// AuthError — thrown on a 401 so MultiCdmApp can surface the app's standard
// unauthenticated popup instead of showing a generic failure.
// -----------------------------------------------------------------------------

export function AuthError() {
  const err = new Error("Authentication Error");
  err.isAuthError = true;
  return err;
}

function isAbort(error) {
  return error && error.name === "AbortError";
}

export default function createMultiCdmApi(options) {
  const opts = options || {};
  const route = opts.route;
  const getUserData = function () {
    return typeof opts.userData === "function"
      ? opts.userData()
      : opts.userData;
  };
  const getUserProfile = function () {
    return typeof opts.userProfile === "function"
      ? opts.userProfile()
      : opts.userProfile;
  };
  const onAuthError = opts.onAuthError;

  function token() {
    const userData = getUserData();
    return userData ? userData.Token : "";
  }

  function customerId() {
    const userData = getUserData();
    return userData ? userData.CustomerId : null;
  }

  // Shared response handling: 401 -> notify host + throw; otherwise text.
  function handle(response) {
    console.log("Handling", response);
    if (response.status === 401) {
      console.log("auth error!", onAuthError);
      if (onAuthError) onAuthError();
      throw AuthError();
    }
    return response.text();
  }

  // Most of the legacy handlers send "text/plain" with a JSON body. Keep that
  // exactly — some of them are picky about the content type.
  function post(path, body, signal) {
    return fetch(route + path, {
      method: "POST",
      headers: {
        Token: token(),
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(body),
      signal: signal,
    }).then(handle);
  }

  function get(path, signal) {
    return fetch(route + path, {
      method: "GET",
      headers: { Token: token() },
      signal: signal,
    }).then(handle);
  }

  function logError(error, label) {
    if (isAbort(error) || (error && error.isAuthError)) return;
    console.log("error", error);
    logFetchError(error, getUserProfile(), getUserData(), label);
  }

  function submitLogs() {
    try {
      fetchSubmitLogs(getUserProfile(), getUserData());
    } catch (e) {
      /* logging is best-effort */
    }
  }

  function parseJson(text) {
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }

  async function listCases(signal) {
    let staticCasesInfo;
    try {
      // Memoized in CaseInfoCache — this only hits the network once per
      // login (shared with App.js's own fetch of the same endpoint), not
      // once per listCases() call.
      staticCasesInfo = await fetchCaseInfo(route, token());
    } catch (error) {
      logError(error, "multiCdm.listCases.staticFetch");
      throw error;
    }

    const staticCases = staticCasesInfo.cases || [];

    return get("/listcasesupdate.webapi", signal)
      .then(function (text) {
        submitLogs();
        const results = JSON.parse(text);
        const caseList = [];
        for (let i = 0; i < results.length; i++) {
          const userCaseData = results[i];
          const staticCaseData = staticCases.find(function (staticCase) {
            return staticCase.CASEID === userCaseData.ID;
          });
          caseList.push(Object.assign({}, userCaseData, staticCaseData));
        }
        return caseList;
      })
      .catch(function (error) {
        logError(error, "multiCdm.listCases");
        throw error;
      });
  }

  // Builds the client-side answerList the same way App.getAnswerList does:
  // one entry per question, either an array of blank strings (fill-in) or an
  // array of { option, selected } pairs (multiple choice).
  function buildAnswerList(simulationData) {
    const answerList = [];
    simulationData.qo.forEach(function (question) {
      if (question.BLANKTYPE) {
        const blanks = [];
        for (let i = 0; i < question.MAXACCEPTEDANSWERS; ++i) {
          blanks.push("");
        }
        answerList.push(blanks);
      } else if (question.MCTYPE) {
        const options = [];
        simulationData.ao.forEach(function (answerOutput) {
          if (answerOutput.QUESTIONID === question.QUESTIONID) {
            options.push({ option: answerOutput.ANSWER, selected: false });
          }
        });
        answerList.push(options);
      }
    });
    return answerList;
  }

  function startCase(caseData, timerSpeed, signal) {
    return post(
      "/startcase.webapi",
      { customerId: customerId(), caseId: caseData.ID },
      signal,
    )
      .then(function (result) {
        submitLogs();

        if (result.includes("Error: ")) {
          const err = new Error(result);
          err.isCaseListError = true;
          throw err;
        }

        const simulationData = JSON.parse(result);

        simulationData.timeRemainingInSeconds =
          simulationData.TOTALMINFORCASE *
          60 *
          timerSpeedMultiplier(timerSpeed);

        // "98.6 degrees F: ..." -> [{ vital, value }], same transform as
        // App.fetchStartCase.
        const rawInfo =
          typeof simulationData.INITIALINFO === "string"
            ? simulationData.INITIALINFO.split(",")
            : simulationData.INITIALINFO || [];
        simulationData.INITIALINFO = rawInfo.map(function (value) {
          const tempValue = value.includes(" degrees ")
            ? value.replace(" degrees ", "\u00b0")
            : value;
          const parts = tempValue.split(": ");
          return { vital: parts[0], value: parts[1] };
        });

        simulationData.answerList = buildAnswerList(simulationData);
        simulationData.currentQuestion = 1;

        // Oldest-first from the backend; reverse for newest-first, same as
        // App.fetchStartCase does before handing the list to its popup.
        const ialist = simulationData.ialist
          ? [...simulationData.ialist].reverse()
          : [];

        return {
          simulationData: simulationData,
          caseguid: simulationData.caseguid,
          ialist: ialist,
        };
      })
      .catch(function (error) {
        logError(error, "multiCdm.startCase");
        throw error;
      });
  }

  function incompleteStart(input, signal) {
    const simulationData = Object.assign({}, input.simulationData);
    simulationData.answerList = simulationData.answerList.map(function (q) {
      return q.slice();
    });

    return post(
      "/incompletestart.webapi",
      {
        CustomerId: customerId(),
        caseguid: input.caseguid,
        CurrentStateId: input.currentStateId,
      },
      signal,
    )
      .then(function (text) {
        submitLogs();
        const data = JSON.parse(text);

        let previousInput = [];
        if (data.PreviousInput) {
          try {
            previousInput = JSON.parse(data.PreviousInput);
          } catch (e) {
            previousInput = [];
          }
        }

        previousInput.forEach(function (inputValue, inputIndex) {
          if (inputValue.CustomerAnswersBlank !== null) {
            simulationData.answerList[inputIndex] = [
              ...inputValue.CustomerAnswersBlank,
            ];
          } else if (inputValue.CustomerAnswerMC) {
            inputValue.CustomerAnswerMC.forEach(function (value) {
              const answerIndex = simulationData.ao.findIndex(
                function (answerOutputValue) {
                  return answerOutputValue.ANSWERID === value;
                },
              );
              if (answerIndex !== -1) {
                simulationData.answerList[inputIndex][answerIndex].selected =
                  true;
              }
            });
          }
        });

        simulationData.currentQuestion = previousInput.length + 1;
        simulationData.timeRemainingInSeconds = data.TimeLeft;

        return { simulationData: simulationData };
      })
      .catch(function (error) {
        logError(error, "multiCdm.incompleteStart");
        throw error;
      });
  }

  function triggerGamificationEvent(caseIds, scorePct, signal) {
    return fetch(route + "/triggergamificationevent.webapi", {
      method: "POST",
      headers: {
        Token: token(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ case_ids: caseIds, score_pct: scorePct }),
      signal: signal,
    })
      .then(function (response) {
        if (response.status === 401) {
          if (onAuthError) onAuthError();
          return null;
        }
        if (!response.ok) return null;
        return response.json();
      })
      .catch(function (error) {
        logError(error, "multiCdm.triggerGamificationEvent");
        return null;
      });
  }

  function createMultiCdmCase(input, signal) {
    return post(
      "/createmulticase.webapi",
      {
        Title: input.title,
        CaseIds: input.caseIds,
        Settings: input.settings,
      },
      signal,
    )
      .then(function (text) {
        return normalizeSession(parseJson(text));
      })
      .catch(function (error) {
        logError(error, "multiCdm.createMultiCdmCase");
        throw error;
      });
  }

  function listMultiCdmCases(signal) {
    return get("/listmulticases.webapi", signal)
      .then(function (text) {
        const data = parseJson(text);
        const rows = Array.isArray(data)
          ? data
          : (data && (data.Sessions || data.MultiCases)) || [];
        return rows.map(normalizeSession);
      })
      .catch(function (error) {
        logError(error, "multiCdm.listMultiCdmCases");
        throw error;
      });
  }

  function getMultiCdmCase(multiCdmCaseId, signal) {
    return post("/getmulticase.webapi", { MultiCaseId: multiCdmCaseId }, signal)
      .then(function (text) {
        return normalizeSession(parseJson(text));
      })
      .catch(function (error) {
        logError(error, "multiCdm.getMultiCdmCase");
        throw error;
      });
  }

  function saveMultiCdmCaseProgress(input, signal) {
    return post(
      "/savemulticaseprogress.webapi",
      {
        MultiCaseId: input.multiCdmCaseId,
        CaseProgress: JSON.stringify(input.caseProgress),
        Completed: !!input.completed,
      },
      signal,
    )
      .then(function (text) {
        const data = parseJson(text);

        if (!data || typeof data === "string") return null;
        return normalizeSession(data);
      })
      .catch(function (error) {
        logError(error, "multiCdm.saveMultiCdmCaseProgress");
        throw error;
      });
  }

  function updateMultiCdmCaseTitle(multiCdmCaseId, title, signal) {
    return post(
      "/updatemulticasetitle.webapi",
      { MultiCdmCaseId: multiCdmCaseId, Title: title },
      signal,
    )
      .then(function (text) {
        return parseJson(text);
      })
      .catch(function (error) {
        logError(error, "multiCdm.updateMultiCdmCaseTitle");
        throw error;
      });
  }

  function retrieveMultiCdmCaseGrade(caseguid, signal) {
    return post(
      "/retreivemulticasegrade.webapi",
      { caseguid: caseguid },
      signal,
    )
      .then(function (text) {
        submitLogs();
        return normalizeGrade(JSON.parse(text));
      })
      .catch(function (error) {
        logError(error, "multiCdm.retrieveMultiCdmCaseGrade");
        throw error;
      });
  }

  function saveReviewLater(caseId, marked, signal) {
    return post(
      "/reviewlater.webapi",
      { customerId: customerId(), caseId: caseId, marked: marked },
      signal,
    ).catch(function (error) {
      logError(error, "multiCdm.saveReviewLater");
      throw error;
    });
  }

  function saveHighYield(caseId, markHighYield, signal) {
    return post(
      "/highyield.webapi",
      {
        customerid: customerId(),
        caseid: caseId,
        markhighyield: markHighYield,
        unmarkhighyield: !markHighYield,
      },
      signal,
    ).catch(function (error) {
      logError(error, "multiCdm.saveHighYield");
      throw error;
    });
  }

  // Reading options — shared with the main grading page and the simulation
  // (skin, text size, confirm before Next, split screen, instructions/lab
  // panels above the question), so a user's choices carry across all of
  // them. The blob is saved whole, so every save merges into the last copy
  // we've seen rather than posting only the fields this caller knows about.
  let lastReadingOptions = null;

  function getReadingOptions(signal) {
    return post(
      "/getsavedreadingoptions.webapi",
      { customerId: customerId() },
      signal,
    )
      .then(function (text) {
        const data = parseJson(text);
        if (data && typeof data === "object") lastReadingOptions = data;
        return data;
      })
      .catch(function (error) {
        logError(error, "multiCdm.getReadingOptions");
        return null;
      });
  }

  function saveReadingOptions(readingOptions, signal) {
    // Haven't loaded the saved blob in this session yet: load it first so
    // the save doesn't drop fields another page wrote.
    const base = lastReadingOptions
      ? Promise.resolve(lastReadingOptions)
      : getReadingOptions(signal);

    return base
      .then(function (saved) {
        const merged = Object.assign(
          {},
          saved && typeof saved === "object" ? saved : {},
          readingOptions,
          { customerId: customerId() },
        );
        lastReadingOptions = merged;
        return post("/savereadingoptions.webapi", merged, signal);
      })
      .then(parseJson)
      .catch(function (error) {
        logError(error, "multiCdm.saveReadingOptions");
        return null;
      });
  }

  // POST /savemulticdmsettings.webapi  { Settings }
  // Remembers the user's settings on the customer record so the next block —
  // on any device — starts from them. Best-effort: a failure here shouldn't
  // interrupt a block, since the block carries its own copy regardless.
  function saveMultiCdmSettings(settings, signal) {
    return post(
      "/savemulticcssettings.webapi",
      { Settings: normalizeSettings(settings) },
      signal,
    )
      .then(parseJson)
      .catch(function (error) {
        logError(error, "multiCdm.saveMultiCdmSettings");
        return null;
      });
  }
  function submitFeedback(input, signal) {
    const body = { customerId: customerId(), feedback: input.feedback };
    if (input.caseId !== null && input.caseId !== undefined) {
      body.caseId = input.caseId;
    }
    if (input.caseDiagnosis !== null && input.caseDiagnosis !== undefined) {
      body.caseDiagnosis = input.caseDiagnosis;
    }
    return post("/feedback.webapi", body, signal).catch(function (error) {
      logError(error, "multiCdm.submitFeedback");
      throw error;
    });
  }

  return {
    // case data
    listCases: listCases,
    // running a case
    startCase: startCase,
    incompleteStart: incompleteStart,
    triggerGamificationEvent: triggerGamificationEvent,
    // sessions
    createMultiCdmCase: createMultiCdmCase,
    listMultiCdmCases: listMultiCdmCases,
    getMultiCdmCase: getMultiCdmCase,
    saveMultiCdmCaseProgress: saveMultiCdmCaseProgress,
    updateMultiCdmCaseTitle: updateMultiCdmCaseTitle,
    // review
    retrieveMultiCdmCaseGrade: retrieveMultiCdmCaseGrade,
    saveReviewLater: saveReviewLater,
    saveHighYield: saveHighYield,
    getReadingOptions: getReadingOptions,
    saveReadingOptions: saveReadingOptions,
    saveMultiCdmSettings: saveMultiCdmSettings,
    submitFeedback: submitFeedback,
  };
}

// -----------------------------------------------------------------------------
// CaseProgress helpers
//
// CaseProgress is stored as an NVARCHAR(MAX) JSON blob. Everything that reads or
// writes it goes through here so the shape stays in one place.
// -----------------------------------------------------------------------------

export const DEFAULT_SETTINGS = {
  EnforceCaseTimeLimit: true,
  TimerSpeed: "1x",
  EnforceBreak: false,
  BreakMinutes: 5,
  HideCaseTitle: false,
  HideCaseCategory: false,
};

// The user's last-used settings, parsed out of CUSTOMER.MultiCdmSettings as it
// arrives on the login payload. Empty string means the customer has never saved
// any (the column is NULL), so fall back to the stock defaults.
//
// A new block starts from these; once created, the block owns its own copy in
// the blob, so editing an old block can't retroactively change a finished one.
export function parseRememberedSettings(raw) {
  if (!raw || typeof raw !== "string" || !raw.trim()) {
    return Object.assign({}, DEFAULT_SETTINGS);
  }
  try {
    return normalizeSettings(JSON.parse(raw));
  } catch (e) {
    // Corrupt column contents shouldn't break the builder.
    return Object.assign({}, DEFAULT_SETTINGS);
  }
}

export function normalizeSettings(raw) {
  const parsed = raw && typeof raw === "object" ? raw : {};
  return {
    EnforceCaseTimeLimit:
      parsed.EnforceCaseTimeLimit !== undefined
        ? !!parsed.EnforceCaseTimeLimit
        : DEFAULT_SETTINGS.EnforceCaseTimeLimit,
    TimerSpeed:
      typeof parsed.TimerSpeed === "string" &&
      TIMER_SPEED_OPTIONS.indexOf(parsed.TimerSpeed) !== -1
        ? parsed.TimerSpeed
        : DEFAULT_SETTINGS.TimerSpeed,
    EnforceBreak:
      parsed.EnforceBreak !== undefined
        ? !!parsed.EnforceBreak
        : DEFAULT_SETTINGS.EnforceBreak,
    BreakMinutes:
      typeof parsed.BreakMinutes === "number" && parsed.BreakMinutes >= 0
        ? parsed.BreakMinutes
        : DEFAULT_SETTINGS.BreakMinutes,
    HideCaseTitle:
      parsed.HideCaseTitle !== undefined
        ? !!parsed.HideCaseTitle
        : DEFAULT_SETTINGS.HideCaseTitle,
    HideCaseCategory:
      parsed.HideCaseCategory !== undefined
        ? !!parsed.HideCaseCategory
        : DEFAULT_SETTINGS.HideCaseCategory,
  };
}

// Build the initial blob at creation time. Every case is seeded as pending so
// the break screen can render the full list before anything has been run.
export function buildCaseProgress(caseIds, title, settings) {
  const now = new Date().toISOString();
  return {
    Title: title,
    CreatedDate: now,
    ModifiedDate: now,
    Settings: normalizeSettings(settings),
    BreakRemainingSeconds: null,
    Cases: caseIds.map(function (caseId) {
      return {
        CaseId: caseId,
        Status: CASE_STATUS.PENDING,
        caseguid: null,
        CurrentStateId: null,
        GradeForCase: null,
        StartedAt: null,
        CompletedAt: null,
        ElapsedSeconds: null,
      };
    }),
  };
}

// Tolerant parse — the blob may arrive as a JSON string or an already-parsed
// object depending on how the handler serializes it. Falls back to rebuilding
// from CasesList if CaseProgress is missing or unreadable.
export function parseCaseProgress(raw, casesList) {
  let parsed = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) parsed = null;
    else {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = null;
      }
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return buildCaseProgress(casesList || [], "", DEFAULT_SETTINGS);
  }

  const cases = Array.isArray(parsed.Cases) ? parsed.Cases : [];
  return {
    Title: parsed.Title || "",
    CreatedDate: parsed.CreatedDate || null,
    ModifiedDate: parsed.ModifiedDate || null,
    Settings: normalizeSettings(parsed.Settings),
    BreakRemainingSeconds:
      typeof parsed.BreakRemainingSeconds === "number"
        ? parsed.BreakRemainingSeconds
        : null,
    Cases: cases.map(function (entry) {
      return {
        CaseId: entry.CaseId,
        Status: entry.Status || CASE_STATUS.PENDING,
        caseguid: entry.caseguid || null,
        CurrentStateId:
          entry.CurrentStateId !== undefined ? entry.CurrentStateId : null,
        GradeForCase:
          typeof entry.GradeForCase === "number" ? entry.GradeForCase : null,
        StartedAt: entry.StartedAt || null,
        CompletedAt: entry.CompletedAt || null,
        ElapsedSeconds:
          typeof entry.ElapsedSeconds === "number"
            ? entry.ElapsedSeconds
            : null,
      };
    }),
  };
}

// A session row as the UI wants it: CasesList and CaseProgress already parsed.
export function normalizeSession(row) {
  if (!row) return null;

  let casesList = row.CasesList;
  if (typeof casesList === "string") {
    try {
      casesList = JSON.parse(casesList);
    } catch (e) {
      casesList = [];
    }
  }
  if (!Array.isArray(casesList)) casesList = [];

  const caseProgress = parseCaseProgress(row.CaseProgress, casesList);

  return {
    ID: row.ID,
    Title: row.Title || caseProgress.Title || "",
    CasesList: casesList,
    CaseProgress: caseProgress,
    Completed: !!row.Completed,
  };
}

// The grade envelope from /retreivemulticdmcasegrade.webapi is the same shape
// as /retreivecasegrade.webapi's response for CDM: no GradeFeedback wrapper,
// no ActionLogList — grading is organized per-question under pgolist.pgolist,
// each with its own GradeOut array coloured via GradeColor ("Correct - Green"
// / "Neutral - Blue" / anything else = incorrect). NewGrading.js itself does
// no client-side sorting or mutation of this data (its old CCS-style
// normalization is dead code, commented out) — this just guards against
// missing arrays so MultiCdmGradeView never has to null-check pgolist itself.
export function normalizeGrade(data) {
  if (!data) return data;

  if (!data.pgolist || !Array.isArray(data.pgolist.pgolist)) {
    data.pgolist = { pgolist: [] };
  }
  if (!Array.isArray(data.qo)) data.qo = [];
  if (!Array.isArray(data.ao)) data.ao = [];

  return data;
}

// --- session-level derivations ----------------------------------------------

export function completedCount(caseProgress) {
  if (!caseProgress || !caseProgress.Cases) return 0;
  return caseProgress.Cases.filter(function (entry) {
    return entry.Status === CASE_STATUS.COMPLETED;
  }).length;
}

// The next case to run: the first entry that isn't finished. Order is fixed at
// creation, like the real exam. Returns null once the block is done.
export function nextCaseEntry(caseProgress) {
  if (!caseProgress || !caseProgress.Cases) return null;
  for (let i = 0; i < caseProgress.Cases.length; i++) {
    if (caseProgress.Cases[i].Status !== CASE_STATUS.COMPLETED) {
      return caseProgress.Cases[i];
    }
  }
  return null;
}

export function isSessionComplete(caseProgress) {
  return nextCaseEntry(caseProgress) === null;
}

// Auto-title: "4 Cases — Jul 15, 2026". Users can rename later.
export function defaultSessionTitle(caseCount, date) {
  const when = date || new Date();
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const stamp =
    MONTHS[when.getMonth()] + " " + when.getDate() + ", " + when.getFullYear();
  return (
    caseCount + (caseCount === 1 ? " Case" : " Cases") + " \u2014 " + stamp
  );
}

// What the break screen can say about a case's timing before it's started.
// Unlike CCS (a static "10 Minute Case" from TIMEMODEID), CDM only learns a
// case's real allowance (TOTALMINFORCASE) from /startcase.webapi, so there's
// no specific minute count to show ahead of time — only whether the block
// enforces a timer, and at what speed.
export function caseTimingLabel(settings) {
  const config = normalizeSettings(settings);
  if (!config.EnforceCaseTimeLimit) return "Untimed";
  return config.TimerSpeed === "1x"
    ? "Timed"
    : "Timed \u00b7 " + config.TimerSpeed + " speed";
}

// Elapsed wall-clock seconds for a case, from StartedAt -> CompletedAt.
export function elapsedSeconds(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (isNaN(start) || isNaN(end) || end < start) return null;
  return Math.round((end - start) / 1000);
}

export function formatElapsed(seconds) {
  if (typeof seconds !== "number" || seconds < 0) return "\u2014";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins + "m " + (secs < 10 ? "0" + secs : secs) + "s";
}

// --- case selection ----------------------------------------------------------

// Fisher-Yates. Random blocks get a random order too.
export function shuffle(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

// A case counts as completed only if it also has a real completion date —
// the same guard the main case list uses ("0001-01-01T00:00:00" means never).
export function isCaseCompleted(caseRow) {
  return (
    !!caseRow.COMPLETED && caseRow.LastCompletionDate !== "0001-01-01T00:00:00"
  );
}

// Random selection. Filters combine; hidden cases are deliberately ignored.
// Demo cases are excluded — demo accounts can't reach MultiCDM at all.
//
//   filters = {
//     includeCompleted: bool,
//     includeIncomplete: bool,
//     categories: [CASECATEGORYDESC],   // empty = all
//   }
//
// Returns { caseIds, error }. Asking for more than the pool holds is an error,
// per spec — we don't silently hand back a shorter block.
export function pickRandomCases(cases, count, filters) {
  const config = filters || {};
  const categories = config.categories || [];

  const pool = cases.filter(function (caseRow) {
    if (caseRow.DEMO === "Yes") return false;

    const completed = isCaseCompleted(caseRow);
    if (completed && !config.includeCompleted) return false;
    if (!completed && !config.includeIncomplete) return false;

    if (
      categories.length > 0 &&
      categories.indexOf(caseRow.CASECATEGORYDESC) === -1
    ) {
      return false;
    }
    return true;
  });

  if (count < MIN_BLOCK_SIZE || count > MAX_BLOCK_SIZE) {
    return {
      caseIds: [],
      error:
        "Pick between " +
        MIN_BLOCK_SIZE +
        " and " +
        MAX_BLOCK_SIZE +
        " cases for a block.",
    };
  }

  if (pool.length < count) {
    return {
      caseIds: [],
      error:
        "Only " +
        pool.length +
        (pool.length === 1 ? " case matches" : " cases match") +
        " those filters, but you asked for " +
        count +
        ". Widen the filters or lower the count.",
    };
  }

  return {
    caseIds: shuffle(pool)
      .slice(0, count)
      .map(function (caseRow) {
        return caseRow.ID;
      }),
    error: null,
  };
}
